import React, { ReactNode, useState } from 'react'

import { TextClamp } from '../../components/text/TextClamp'
import { ChartLabel } from '../_shared/ChartLabel'
import { FastTooltip } from '../_shared/FastTooltip'
import { DECK_CHART_PRIMARY_COLOR } from '../../foundations/chart-colors'
import { CATEGORICAL_NEUTRAL } from '../categorical-share/style'
import { OtherToggle, OtherToggleLabels, freeRoomInParent, isOtherExpandable, useOtherExpand } from '../_shared/OtherExpand'
import { SectionHeader, sectionOpenedAt } from '../_shared/SectionHeader'
import { choiceFigure, fillChoiceTemplate, formatChoiceShare } from './format'

import { ChoiceBarsItem, ChoiceBarsSeries, PlotHighlight, PlotRenderMode } from '@digital-twins/slide-schemas/generated/plot-types'
import { PlotDataOf, StoredPlotPayload } from '../contract'

/**
 * The choice-bars plot (plots plan J): horizontal share bars for one choice
 * question, one series at a time.
 *
 * Python stamps every figure: the share (rounded once), the order, the Other
 * rollup and its members, the winner (`topIds`, every tied top option) and the
 * images. The plot draws them and decides nothing: no re-sort, no re-round, no
 * crown of its own, no minimum bar width (a zero-pick option is a real 0%).
 *
 * Two sizes:
 * - `slide`: today's single/multi-select bar rows pixel for pixel (label above a
 *   21 px track, 72 px value column, 28 px thumbnail, compact rows past five;
 *   past eight, up to ten, tighter gaps and a 16 px track).
 * - `card`: the summary card and survey tab list (today's `DistributionBars` look).
 *
 * Sections (wave 7, Q12): a card whose payload stamps `data.groups` draws each
 * group's header above its rows, rows as given (Python stamps them adjacent and
 * in group order; the Other row last, ungrouped). The header's tone tints the
 * header only, never a bar.
 *
 * "Other" opens in place (plots plan OT, decision 23): with the host's words
 * and an interactive render, a link under the plot opens the stamped
 * `otherMembers` as neutral rows under the Other row; the plot box keeps its
 * closed height and scrolls. That link replaces the hover list (one
 * disclosure). Static renders and hosts without words draw today's plot.
 */

export type ChoiceBarsPlotData = StoredPlotPayload<'choice-bars'>['data'] | PlotDataOf<'choice-bars'>

export type ChoiceBarsPlotSize = 'slide' | 'card'

export interface ChoiceBarsPlotLabelArgs {
  item: ChoiceBarsItem
  index: number
  maxLines: number
  style: React.CSSProperties
  /** The shared truncation rule's (i) for this option (plots plan LB). */
  chartLabel: { hintIndex: number }
}

export interface ChoiceBarsPlotProps {
  data: ChoiceBarsPlotData
  size?: ChoiceBarsPlotSize
  /** The series to draw; the first when absent. */
  seriesId?: string
  highlight?: PlotHighlight
  renderMode?: PlotRenderMode
  locale?: string | null
  /** The deck passes its editable text; everyone else gets a clamped label with hover. */
  renderLabel?: (args: ChoiceBarsPlotLabelArgs) => ReactNode
  /** Legacy stored payloads print today's figure string; new payloads never set it. */
  formatShare?: (share: number) => string
  /** Print every row's share at exactly the stamped decimals ("52.0%"); opened Other members keep their own. Default off. */
  fixedShareDecimals?: boolean
  /** Survey tab: print the stamped count. Default percent. Widths stay shares. */
  shareUnit?: 'percent' | 'count'
  /** Row test ids: the deck keeps `single-select-bar-row` / `multi-select-bar-row`. */
  testIdPrefix?: string
  /** Legacy stored slides title their Other tooltip; new payloads list the members only. */
  otherMembersHeading?: string | null
  /** Card only: the caller's label formatter for legacy stored cards (the frontend's `formatLabel`). */
  formatItemLabel?: (label: string) => string
  /** Card only: a note printed above the plot (e.g. the scale's "1-5 scale"). */
  note?: string | null
  /** The "Other" link's words in the viewer's UI language (decision 23); no link without them. */
  otherToggleLabels?: OtherToggleLabels | null
}

const DARK = 'var(--slide-cover-bg)'
const TOP_FILL = DECK_CHART_PRIMARY_COLOR
const REST_FILL = 'var(--slide-chart-6)'
/** "Other" is always neutral (Rule defaults, Colours). */
const OTHER_FILL = 'var(--ds-gray-500, #a0aec0)'
const SLIDE_TRACK = 'var(--slide-neutral-light)'
const CARD_TRACK = 'hsl(var(--chart-bar-track, 0 0% 90%))'
/** Past this many rows the slide compacts its rows (today's `dense`). */
const SLIDE_DENSE_AFTER = 5
/** Past the 6-8 dense band, up to the slide's ten rows (B5): tighter gaps and a thinner track, so ten fit the panel. */
const SLIDE_COMPACT_AFTER = 8

function pickSeries(data: ChoiceBarsPlotData, seriesId?: string): ChoiceBarsSeries | null {
  const series = data.series ?? []
  if (seriesId) {
    return series.find((entry) => entry.id === seriesId) ?? series[0] ?? null
  }
  return series[0] ?? null
}

/** `uniform`: no winner mark (highlight none), every option in the primary colour as today's card. An escape row uses the categorical Other neutral, even when it would otherwise be the top or uniform fill. */
function fillFor(item: ChoiceBarsItem, isTop: boolean, uniform = false): string {
  if (item.isEscape) {
    return CATEGORICAL_NEUTRAL
  }
  if (item.role === 'other') {
    return OTHER_FILL
  }
  return isTop || uniform ? TOP_FILL : REST_FILL
}

/** The stamped winner. An escape row is never crowned, even when its id is in `topIds`. */
function rowIsTop(item: ChoiceBarsItem, topIds: Set<string>): boolean {
  if (item.isEscape) {
    return false
  }
  return topIds.has(item.id)
}

function widthPct(share: number): number {
  return Math.max(0, Math.min(100, share))
}

/** The slide's fill: today's 2% floor keeps a small share visible; an unpicked option (0%) draws no fill. */
function slideWidthPct(share: number): number {
  return share > 0 ? Math.max(2, widthPct(share)) : 0
}

/** Members a hover list names: zero picks show in the opened list only (decision 23, owner default). */
function heldMembers(item: ChoiceBarsItem): NonNullable<ChoiceBarsItem['otherMembers']> {
  return (item.otherMembers ?? []).filter((member) => member.share > 0)
}


function OtherMembers(props: { item: ChoiceBarsItem; heading?: string | null; format: (entry: { share: number; count?: number | null }) => string }) {
  const { item, heading, format } = props
  const members = heldMembers(item)
  return (
    <div
      role="tooltip"
      data-testid="choice-bars-other-members"
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        marginBottom: 8,
        background: '#1a1a1a',
        color: '#ffffff',
        borderRadius: 8,
        padding: '12px 16px',
        fontSize: 14,
        lineHeight: '1.6',
        whiteSpace: 'nowrap',
        zIndex: 10,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      {heading ? <div style={{ fontWeight: 600, marginBottom: 4 }}>{heading}</div> : null}
      {members.map((member) => (
        <div key={member.id}>
          {member.label}: {format(member)}
        </div>
      ))}
    </div>
  )
}

/**
 * The Other row's members drawn as neutral rows under it (decision 23,
 * opened): every member, zero picks included. Labels follow the one chart
 * label rule (decision 24): whole, or an ellipsis and the (i), whose hint
 * indices follow the drawn rows' (`firstHint` on).
 */
function OpenedMembers(props: {
  item: ChoiceBarsItem
  size: ChoiceBarsPlotSize
  dense: boolean
  format: (entry: { share: number; count?: number | null }) => string
  testIdPrefix: string
  firstHint: number
}) {
  const { item, size, dense, format, testIdPrefix, firstHint } = props

  const members = item.otherMembers ?? []
  if (size === 'slide') {
    const labelLines = dense ? 1 : 2
    return (
      <div data-testid={`${testIdPrefix}-other-members`} style={{ display: 'flex', flexDirection: 'column', gap: dense ? 14 : 28 }}>
        {members.map((member, index) => (
          <div key={member.id} data-testid={`${testIdPrefix}-other-member-${index}`} data-role="other-member" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <ChartLabel text={member.label} hintIndex={firstHint + index} maxLines={labelLines} style={{ fontSize: 16, color: DARK, minWidth: 0 }} />
              </div>
              <span style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.2, color: DARK, textAlign: 'right', width: 72, flexShrink: 0 }}>{format(member)}</span>
            </div>
            <div style={{ height: 21, borderRadius: 4, background: SLIDE_TRACK, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${slideWidthPct(member.share)}%`, borderRadius: 4, background: OTHER_FILL }} />
            </div>
          </div>
        ))}
      </div>
    )
  }
  return (
    <div data-testid={`${testIdPrefix}-other-members`} className="space-y-5">
      {members.map((member, index) => (
        <div key={member.id} className="space-y-1.5" data-testid={`${testIdPrefix}-other-member-${index}`} data-role="other-member">
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-2 min-w-0">
              <ChartLabel text={member.label} hintIndex={firstHint + index} maxLines={2} className="text-[14px] font-normal text-foreground min-w-0" />
            </span>
            <span className="text-sm font-semibold tabular-nums shrink-0">{format(member)}</span>
          </div>
          <span className="relative block h-[10px] w-full rounded-full" style={{ backgroundColor: CARD_TRACK }}>
            <span className="absolute inset-0 rounded-full" style={{ width: `${widthPct(member.share)}%`, backgroundColor: OTHER_FILL }} />
          </span>
        </div>
      ))}
    </div>
  )
}

interface RowProps {
  item: ChoiceBarsItem
  index: number
  isTop: boolean
  dense: boolean
  /** Past `SLIDE_COMPACT_AFTER` rows (slide only). */
  compact?: boolean
  showImage: boolean
  format: (entry: { share: number; count?: number | null }) => string
  renderLabel?: ChoiceBarsPlotProps['renderLabel']
  testIdPrefix: string
  otherMembersHeading?: string | null
  interactive: boolean
  formatItemLabel?: (label: string) => string
  uniform?: boolean
  /** The Other link is drawn: no hover list or member title (one disclosure). */
  hasOtherLink?: boolean
  /** Formats an Other member's share; the row's own `format` when absent. */
  memberFormat?: (entry: { share: number; count?: number | null }) => string
}

function SlideRow(props: RowProps) {
  const { item, index, isTop, dense, compact, showImage, format, renderLabel, testIdPrefix, otherMembersHeading, interactive } = props
  const renderMode = interactive ? 'interactive' : 'static'

  const [showMembers, setShowMembers] = useState(false)

  const hasMembers = interactive && !props.hasOtherLink && heldMembers(item).length > 0
  const labelLines = dense ? 1 : 2
  const labelStyle: React.CSSProperties = { fontSize: 16, color: DARK, minWidth: 0 }
  const label = renderLabel ? (
    renderLabel({ item, index, maxLines: labelLines, style: labelStyle, chartLabel: { hintIndex: index } })
  ) : (
    <ChartLabel text={item.label} hintIndex={index} maxLines={labelLines} renderMode={renderMode} style={labelStyle} />
  )

  return (
    <div
      data-testid={`${testIdPrefix}-bar-row`}
      data-role={item.role}
      data-top={isTop ? 'true' : undefined}
      style={{ display: 'flex', flexDirection: 'column', gap: compact ? 4 : 8, position: 'relative' }}
      onMouseEnter={hasMembers ? () => setShowMembers(true) : undefined}
      onMouseLeave={hasMembers ? () => setShowMembers(false) : undefined}
    >
      {showMembers ? <OtherMembers item={item} heading={otherMembersHeading} format={format} /> : null}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {showImage && item.imageUrl ? (
            <span style={{ position: 'relative', width: 28, height: 28, flexShrink: 0 }}>
              <img
                src={item.imageUrl}
                alt={item.label}
                data-testid="bar-row-thumbnail"
                style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', display: 'block' }}
              />
              {item.previewKind === 'video' ? <ChoicePlayGlyph size={12} /> : null}
            </span>
          ) : null}
          {label}
        </div>
        <TextClamp
          minLines={1}
          maxLines={1}
          lineHeight="1.2"
          style={{ fontSize: 18, fontWeight: 600, color: DARK, textAlign: 'right', width: 72, flexShrink: 0 }}
        >
          <span data-testid={`${testIdPrefix}-bar-value-${index}`}>{format(item)}</span>
        </TextClamp>
      </div>
      <div style={{ height: compact ? 16 : 21, borderRadius: 4, background: SLIDE_TRACK, overflow: 'hidden' }}>
        <div
          data-testid={`${testIdPrefix}-bar-fill-${index}`}
          style={{ height: '100%', width: `${slideWidthPct(item.share)}%`, borderRadius: 4, background: fillFor(item, isTop) }}
        />
      </div>
    </div>
  )
}

function CardRow(props: RowProps) {
  const { item, index, isTop, showImage, format, testIdPrefix, formatItemLabel, uniform } = props
  const memberFormat = props.memberFormat ?? format

  const members = props.hasOtherLink ? [] : heldMembers(item)
  const membersTitle = members.length ? members.map((member) => `${member.label}: ${memberFormat(member)}`).join('\n') : undefined

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-row-${index}`} data-role={item.role} data-top={isTop ? 'true' : undefined}>
      <div className="flex items-center justify-between gap-4">
        {/* A static card (PDF, image) keeps the native title; only an interactive one takes the fast tooltip. */}
        <FastTooltip label={props.interactive ? membersTitle : undefined}>
        <span className="flex items-center gap-2 min-w-0" title={props.interactive ? undefined : membersTitle}>
          {showImage && item.imageUrl ? (
            <span style={{ position: 'relative', width: 28, height: 28, flexShrink: 0 }}>
              <img
                src={item.imageUrl}
                alt={item.label}
                data-testid={`${testIdPrefix}-thumbnail-${index}`}
                style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', display: 'block' }}
              />
              {item.previewKind === 'video' ? <ChoicePlayGlyph size={12} /> : null}
            </span>
          ) : null}
          <span className="text-[14px] font-normal text-foreground" data-testid={`${testIdPrefix}-label-${index}`}>
            {formatItemLabel ? formatItemLabel(item.label) : item.label}
          </span>
        </span>
        </FastTooltip>
        <span className="text-sm font-semibold tabular-nums shrink-0" data-testid={`${testIdPrefix}-value-${index}`}>
          {format(item)}
        </span>
      </div>
      <span className="relative block h-[10px] w-full rounded-full" style={{ backgroundColor: CARD_TRACK }}>
        <span
          className="absolute inset-0 rounded-full"
          data-testid={`${testIdPrefix}-fill-${index}`}
          style={{ width: `${widthPct(item.share)}%`, backgroundColor: fillFor(item, isTop, uniform) }}
        />
      </span>
    </div>
  )
}

/** The play glyph over a video poster (Rule defaults, Images). */
function ChoicePlayGlyph(props: { size: number }) {
  const { size } = props
  return (
    <span
      aria-hidden="true"
      data-testid="choice-bars-play-glyph"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <svg width={size} height={size} viewBox="0 0 12 12">
        <circle cx="6" cy="6" r="6" fill="rgba(0,0,0,0.55)" />
        <path d="M4.6 3.6 L8.6 6 L4.6 8.4 Z" fill="#ffffff" />
      </svg>
    </span>
  )
}

export function ChoiceBarsPlot(props: ChoiceBarsPlotProps) {
  const {
    data,
    size = 'card',
    seriesId,
    highlight = 'top',
    renderMode = 'interactive',
    locale,
    renderLabel,
    formatShare,
    fixedShareDecimals = false,
    shareUnit = 'percent',
    testIdPrefix = 'choice-bars',
    otherMembersHeading,
    formatItemLabel,
    note,
    otherToggleLabels,
  } = props

  // A slide's box grows into the panel's free room below the link, then scrolls.
  const other = useOtherExpand({ growRoom: size === 'slide' ? () => freeRoomInParent(other.boxRef.current) : undefined })

  const series = pickSeries(data, seriesId)
  const items = series?.items ?? []
  const topIds = new Set(highlight === 'top' ? series?.topIds ?? [] : [])
  const shareFormat = formatShare ?? ((share: number) => formatChoiceShare(share, data.decimals, locale, fixedShareDecimals))
  const format = (entry: { share: number; count?: number | null }) => choiceFigure(entry, shareUnit, shareFormat, locale)
  // Opened "Other" members carry no exact share, so they stay at the shares Python stamped (never padded).
  const memberShareFormat = formatShare ?? ((share: number) => formatChoiceShare(share, data.decimals, locale))
  const memberFormat = (entry: { share: number; count?: number | null }) => choiceFigure(entry, shareUnit, memberShareFormat, locale)
  // Images rule: Python stamps each option row's own picture
  // (`selection.board_images`); the plot draws every one it carries.
  const showImage = items.some((item) => item.role === 'option' && Boolean(item.imageUrl))
  // Decision 23: the link opens the stamped members; nothing is computed here.
  // Only the Other row carries members (a stored deck's adapter leaves its role `option`).
  const otherItem = items.find((item) => (item.otherMembers?.length ?? 0) > 0)
  const hasOtherLink = isOtherExpandable(renderMode, otherToggleLabels, Boolean(otherItem))
  const otherCount = items.length - 1 + (otherItem?.otherMembers?.length ?? 0)
  const otherLink =
    hasOtherLink && otherToggleLabels ? (
      <OtherToggle state={other} labels={otherToggleLabels} count={otherCount} testIdPrefix={testIdPrefix} fontSize={size === 'slide' ? 14 : 12} style={size === 'slide' ? { marginTop: 12, flexShrink: 0 } : undefined} />
    ) : null
  const groups = new Map((data.groups ?? []).map((group) => [group.id, group]))
  const truncationNote = data.truncation
    ? fillChoiceTemplate(data.labels.n_of_total_shown, { shown: data.truncation.shown, total: data.truncation.total })
    : null

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center text-muted-foreground min-h-48" data-testid={`${testIdPrefix}-empty`}>
        {data.labels.plot_no_data ?? null}
      </div>
    )
  }

  if (size === 'slide') {
    const dense = items.length > SLIDE_DENSE_AFTER
    const compact = items.length > SLIDE_COMPACT_AFTER
    const rowGap = compact ? 8 : dense ? 14 : 28
    const rows = items.map((item, index) => (
      <React.Fragment key={item.id}>
        <SlideRow
          item={item}
          index={index}
          isTop={rowIsTop(item, topIds)}
          dense={dense}
          compact={compact}
          showImage={showImage}
          format={format}
          renderLabel={renderLabel}
          testIdPrefix={testIdPrefix}
          otherMembersHeading={otherMembersHeading}
          interactive={renderMode !== 'static'}
          hasOtherLink={hasOtherLink}
        />
        {otherLink && other.isOpen && item === otherItem ? <OpenedMembers item={item} size={size} dense={dense} format={memberFormat} testIdPrefix={testIdPrefix} firstHint={items.length} /> : null}
      </React.Fragment>
    ))
    if (!otherLink) {
      return (
        <div
          data-testid={`${testIdPrefix}-bars-chart`}
          style={{ display: 'flex', flexDirection: 'column', gap: rowGap, flexShrink: 0 }}
        >
          {rows}
        </div>
      )
    }
    return (
      <>
        <div
          ref={other.boxRef}
          id={other.boxId}
          data-testid={`${testIdPrefix}-bars-chart`}
          style={{ display: 'flex', flexDirection: 'column', gap: rowGap, flexShrink: 0, ...other.boxStyle }}
        >
          {rows}
        </div>
        {otherLink}
      </>
    )
  }

  const card = (
    <div
      ref={otherLink ? other.boxRef : undefined}
      id={otherLink ? other.boxId : undefined}
      className="w-full space-y-5"
      data-testid={testIdPrefix}
      style={otherLink ? other.boxStyle : undefined}
    >
      {note ? (
        <span className="block text-[10px] text-muted-foreground" data-testid={`${testIdPrefix}-note`}>
          {note}
        </span>
      ) : null}
      {data.multiSelect && data.labels.multi_select_note ? (
        <span className="block text-[10px] text-muted-foreground" data-testid={`${testIdPrefix}-multi-select-note`}>
          {data.labels.multi_select_note}
        </span>
      ) : null}
      {truncationNote ? (
        <span className="block text-[10px] text-muted-foreground" data-testid={`${testIdPrefix}-truncation`}>
          {truncationNote}
        </span>
      ) : null}
      {items.map((item, index) => {
        const opened = sectionOpenedAt(items, index, groups)
        return (
        <React.Fragment key={item.id}>
          {opened ? <SectionHeader group={opened} testIdPrefix={testIdPrefix} /> : null}
          <CardRow
            item={item}
            index={index}
            isTop={rowIsTop(item, topIds)}
            dense={false}
            showImage={showImage}
            format={format}
            testIdPrefix={testIdPrefix}
            interactive={renderMode !== 'static'}
            formatItemLabel={formatItemLabel}
            uniform={highlight === 'none'}
            hasOtherLink={hasOtherLink}
            memberFormat={memberFormat}
          />
          {otherLink && other.isOpen && item === otherItem ? <OpenedMembers item={item} size={size} dense={false} format={memberFormat} testIdPrefix={testIdPrefix} firstHint={items.length} /> : null}
        </React.Fragment>
        )
      })}
    </div>
  )
  if (!otherLink) {
    return card
  }
  return (
    <div className="flex w-full flex-col gap-3">
      {card}
      {otherLink}
    </div>
  )
}
